import { v4 } from '@lukeed/uuid';
import { original } from 'immer';
import { xFindClosestIndex } from 'ml-spectra-processing';
import { contoursManager } from '../../../data/data2d/Spectrum2D/contours';
import { getYScale, getXScale } from '../../1d/utilities/scale';
import { LAYOUT } from '../../2d/utilities/DimensionLayout';
import { get2DYScale } from '../../2d/utilities/scale';
import { options } from '../../toolbar/ToolTypes';
import groupByInfoKey from '../../utility/GroupByInfoKey';
import { getSpectraByNucleus } from '../../utility/getSpectraByNucleus';
import { rangeStateInit } from '../Reducer';
import { DISPLAYER_MODE, MARGIN } from '../core/Constants';
import { setZoom, wheelZoom, ZoomType } from '../helper/Zoom1DManager';
import zoomHistoryManager from '../helper/ZoomHistoryManager';
import { getActiveSpectra } from '../helper/getActiveSpectra';
import { getActiveSpectrum } from '../helper/getActiveSpectrum';
import { getVerticalAlign } from '../helper/getVerticalAlign';
import { setDomain, setIntegralsYDomain, setMode, } from './DomainActions';
import { calculateBaseLineCorrection, resetSpectrumByFilter, setFilterChanges, } from './FiltersActions';
import { changeSpectrumVerticalAlignment } from './PreferencesActions';
function resetTool(draft, setDefaultTool = true) {
    // reset temp range
    setSelectedOptionPanel(draft, null);
    if (setDefaultTool) {
        draft.toolOptions.selectedTool = options.zoom.id;
    }
    draft.toolOptions.data.baselineCorrection = { zones: [], options: [] };
    if (draft.toolOptions.data.activeFilterID) {
        resetSpectrumByFilter(draft);
    }
    if (draft.tempData) {
        draft.tempData = null;
        setDomain(draft);
    }
}
function resetSelectedTool(draft, filterOnly = false) {
    if ((draft.toolOptions.selectedTool &&
        options[draft.toolOptions.selectedTool].isFilter) ||
        !filterOnly) {
        resetTool(draft);
    }
}
function setSelectedTool(draft, action) {
    const { selectedTool } = action.payload;
    if (draft?.data.length > 0) {
        if (selectedTool) {
            // start Range edit mode
            if (selectedTool === options.editRange.id) {
                const activeSpectrum = getActiveSpectrum(draft);
                if (activeSpectrum) {
                    const range = draft.view.ranges.find((r) => r.spectrumID === activeSpectrum?.id);
                    if (range) {
                        range.showMultiplicityTrees = true;
                    }
                    else {
                        draft.view.ranges.push({
                            spectrumID: activeSpectrum.id,
                            ...rangeStateInit,
                            showMultiplicityTrees: true,
                        });
                    }
                }
            }
            if (selectedTool !== draft.toolOptions.selectedTool) {
                resetTool(draft, false);
            }
            draft.toolOptions.selectedTool = selectedTool;
            if (options[selectedTool].hasOptionPanel) {
                setSelectedOptionPanel(draft, selectedTool);
            }
            if (options[selectedTool].isFilter) {
                setFilterChanges(draft, selectedTool);
            }
        }
        else {
            resetTool(draft, false);
        }
        setMargin(draft);
    }
}
function setSelectedOptionPanel(draft, selectedOptionPanel) {
    draft.toolOptions.selectedOptionPanel = selectedOptionPanel;
}
function setSpectrumsVerticalAlign(draft) {
    const currentVerticalAlign = getVerticalAlign(draft);
    const verticalAlign = ['stack', 'bottom'].includes(currentVerticalAlign)
        ? 'center'
        : 'bottom';
    changeSpectrumVerticalAlignment(draft, { verticalAlign });
}
function handleChangeSpectrumDisplayMode(draft) {
    const currentVerticalAlign = getVerticalAlign(draft);
    const verticalAlign = currentVerticalAlign === 'stack' ? 'bottom' : 'stack';
    changeSpectrumVerticalAlignment(draft, { verticalAlign });
}
function handleAddBaseLineZone(draft, { from, to }) {
    const scaleX = getXScale(draft);
    let start = scaleX.invert(from);
    const end = scaleX.invert(to);
    let zone = [];
    if (start > end) {
        zone = [end, start];
    }
    else {
        zone = [start, end];
    }
    const zones = draft.toolOptions.data.baselineCorrection.zones;
    zones.push({
        id: v4(),
        from: zone[0],
        to: zone[1],
    });
    draft.toolOptions.data.baselineCorrection.zones = zones.slice();
    calculateBaseLineCorrection(draft);
}
function handleDeleteBaseLineZone(draft, id) {
    const state = original(draft);
    draft.toolOptions.data.baselineCorrection.zones =
        state.toolOptions.data.baselineCorrection.zones.filter((zone) => zone.id !== id);
    calculateBaseLineCorrection(draft);
}
function handleToggleRealImaginaryVisibility(draft) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum != null) {
        const { index } = activeSpectrum;
        const datum = draft.data[index];
        datum.display.isRealSpectrumVisible = !datum.display.isRealSpectrumVisible;
        setDomain(draft);
    }
}
function handleBrushEnd(draft, action) {
    const is2D = draft.displayerMode === DISPLAYER_MODE.DM_2D;
    const { height, margin, yDomain, yDomains, width, xDomains, xDomain, mode, displayerMode, } = draft;
    const xScale = getXScale({ width, xDomains, xDomain, mode, margin });
    const verticalAlign = getVerticalAlign(draft);
    const yScale = is2D
        ? get2DYScale(draft)
        : getYScale({ height, margin, yDomain, yDomains, verticalAlign });
    const startX = xScale.invert(action.startX);
    const endX = xScale.invert(action.endX);
    const startY = yScale.invert(action.startY);
    const endY = yScale.invert(action.endY);
    const domainX = startX > endX ? [endX, startX] : [startX, endX];
    const domainY = startY > endY ? [endY, startY] : [startY, endY];
    const brushHistory = zoomHistoryManager(
    // eslint-disable-next-line unicorn/consistent-destructuring
    draft.zoom.history, 
    // eslint-disable-next-line unicorn/consistent-destructuring
    draft.view.spectra.activeTab);
    if (displayerMode === DISPLAYER_MODE.DM_2D) {
        switch (action.trackID) {
            case LAYOUT.CENTER_2D:
                draft.xDomain = domainX;
                draft.yDomain = domainY;
                break;
            case LAYOUT.TOP_1D:
                draft.xDomain = domainX;
                break;
            case LAYOUT.LEFT_1D:
                draft.yDomain = domainY;
                break;
            default:
                break;
        }
        if (brushHistory) {
            brushHistory.push({ xDomain, yDomain });
        }
    }
    else {
        draft.xDomain = domainX;
        if (brushHistory) {
            brushHistory.push({ xDomain: domainX, yDomain: domainY });
        }
    }
}
function setVerticalIndicatorXPosition(draft, position) {
    const activeSpectrum = getActiveSpectrum(draft);
    if (activeSpectrum?.id) {
        const scaleX = getXScale(draft);
        const value = scaleX.invert(position);
        const datum = draft.data[activeSpectrum.index];
        const index = xFindClosestIndex(datum.data.x, value);
        draft.toolOptions.data.pivot = { value, index };
    }
}
function getSpectrumID(draft, index) {
    const { activeSpectra, activeTab } = draft.view.spectra;
    const spectra = activeSpectra[activeTab.split(',')[index]];
    if (spectra?.length === 1) {
        return spectra[0].id;
    }
    return null;
}
function handleZoom(draft, action) {
    const { event, trackID, selectedTool } = action;
    const { view: { ranges: rangeState }, displayerMode, yDomains, integralsYDomains, } = draft;
    const activeSpectra = getActiveSpectra(draft);
    if (displayerMode === DISPLAYER_MODE.DM_2D) {
        const index = trackID === LAYOUT.TOP_1D ? 0 : trackID === LAYOUT.LEFT_1D ? 1 : null;
        if (index !== null) {
            const id = getSpectrumID(draft, index);
            if (id) {
                const domain = yDomains[id];
                yDomains[id] = wheelZoom(event, domain);
            }
        }
    }
    else if (activeSpectra && activeSpectra?.length > 0) {
        // rescale the active spectra integrals;
        if (selectedTool === options.integral.id && event.shiftKey) {
            for (const activeSpectrum of activeSpectra) {
                //check if the integrals is visible
                const { showRangesIntegrals } = rangeState.find((r) => r.spectrumID === activeSpectrum?.id) ||
                    rangeStateInit;
                const domain = integralsYDomains?.[activeSpectrum?.id];
                if (showRangesIntegrals && domain) {
                    integralsYDomains[activeSpectrum?.id] = wheelZoom(event, domain);
                }
            }
        }
        else {
            // rescale the active spectra
            for (const activeSpectrum of activeSpectra) {
                const domain = yDomains?.[activeSpectrum?.id];
                if (domain) {
                    yDomains[activeSpectrum?.id] = wheelZoom(event, domain);
                }
            }
        }
    }
    else {
        // rescale the spectra
        for (const key of Object.keys(yDomains)) {
            const domain = yDomains[key];
            yDomains[key] = wheelZoom(event, domain);
        }
    }
}
function zoomOut(draft, action) {
    if (draft?.data.length > 0) {
        const { zoomType, trackID } = action;
        const zoomHistory = zoomHistoryManager(draft.zoom.history, draft.view.spectra.activeTab);
        if (draft.displayerMode === DISPLAYER_MODE.DM_1D) {
            switch (zoomType) {
                case ZoomType.HORIZONTAL: {
                    draft.xDomain = draft.originDomain.xDomain;
                    zoomHistory.clear();
                    break;
                }
                case ZoomType.VERTICAL:
                    setZoom(draft, { scale: 0.8 });
                    break;
                case ZoomType.STEP_HORIZONTAL: {
                    const zoomValue = zoomHistory.pop();
                    draft.xDomain = zoomValue
                        ? zoomValue.xDomain
                        : draft.originDomain.xDomain;
                    setZoom(draft, { scale: 0.8 });
                    break;
                }
                default: {
                    draft.xDomain = draft.originDomain.xDomain;
                    setZoom(draft, { scale: 0.8 });
                    break;
                }
            }
        }
        else {
            const { xDomain, yDomain, yDomains } = draft.originDomain;
            if ([LAYOUT.TOP_1D, LAYOUT.LEFT_1D, LAYOUT.CENTER_2D].includes(trackID)) {
                const zoomValue = zoomHistory.pop();
                draft.xDomain = zoomValue ? zoomValue.xDomain : xDomain;
                draft.yDomain = zoomValue ? zoomValue.yDomain : yDomain;
            }
            else {
                zoomHistory.clear();
                draft.xDomain = xDomain;
                draft.yDomain = yDomain;
                draft.yDomains = yDomains;
            }
        }
    }
}
function hasAcceptedSpectrum(draft, index) {
    const { activeTab, activeSpectra } = draft.view.spectra;
    const nuclei = activeTab.split(',');
    const spectra = activeSpectra[nuclei[index]];
    if (spectra?.length === 1) {
        const activeSpectrum = spectra[0];
        return (activeSpectrum?.id &&
            !draft.data[activeSpectrum.index].info.isFid);
    }
    return false;
}
function setMargin(draft) {
    const activeSpectrum = getActiveSpectrum(draft);
    const spectrum = (activeSpectrum?.id && draft.data[activeSpectrum.index]) || null;
    if (draft.displayerMode === DISPLAYER_MODE.DM_2D &&
        (draft.toolOptions.selectedTool === options.slicing.id ||
            spectrum?.info.isFid)) {
        draft.margin = MARGIN['2D'];
    }
    else if (draft.displayerMode === DISPLAYER_MODE.DM_2D) {
        const top = hasAcceptedSpectrum(draft, 0)
            ? MARGIN['2D'].top
            : MARGIN['1D'].top;
        const left = hasAcceptedSpectrum(draft, 1)
            ? MARGIN['2D'].left
            : MARGIN['1D'].left;
        draft.margin = { ...MARGIN['2D'], top, left };
    }
    else if (draft.displayerMode === DISPLAYER_MODE.DM_1D) {
        draft.margin = MARGIN['1D'];
    }
}
function setDisplayerMode(draft, data) {
    draft.displayerMode =
        data && data.some((d) => d.info.dimension === 2)
            ? DISPLAYER_MODE.DM_2D
            : DISPLAYER_MODE.DM_1D;
}
function setTabActiveSpectrum(draft, dataGroupByTab) {
    let tabs2D = [];
    const tabActiveSpectrum = {};
    const tabkeys = Object.keys(dataGroupByTab).sort((a, b) => a.split(',').length > b.split(',').length ? -1 : 1);
    for (let tabKey of tabkeys) {
        const data = dataGroupByTab[tabKey];
        const nucleusLength = tabKey.split(',').length;
        if (nucleusLength === 2) {
            tabs2D.push(tabKey);
        }
        if (data.length === 1) {
            const index = draft.data.findIndex((datum) => datum.id === data[0].id);
            tabActiveSpectrum[tabKey] = [{ id: data[0].id, index }];
        }
        else {
            const tabSpectra = dataGroupByTab[tabKey];
            const tabSpectraLength = tabSpectra.length;
            if (tabSpectraLength >= 2) {
                const FTSpectrums = tabSpectra.filter((d) => !d.info.isFid);
                if (FTSpectrums.length > 0 &&
                    (nucleusLength === 2 ||
                        (nucleusLength === 1 && tabSpectraLength !== FTSpectrums.length))) {
                    const index = draft.data.findIndex((datum) => datum.id === FTSpectrums[0].id);
                    tabActiveSpectrum[tabKey] = [{ id: FTSpectrums[0].id, index }];
                }
                else {
                    tabActiveSpectrum[tabKey] = null;
                }
            }
            else {
                tabActiveSpectrum[tabKey] = null;
            }
        }
    }
    draft.view.spectra.activeSpectra = tabActiveSpectrum;
    return tabs2D;
}
function setTab(draft, dataGroupByTab, tab, refresh = false) {
    const groupByTab = Object.keys(dataGroupByTab).sort((a, b) => a.split(',').length > b.split(',').length ? -1 : 1);
    if (JSON.stringify(groupByTab) !==
        JSON.stringify(Object.keys(draft.view.spectra.activeSpectra)) ||
        refresh) {
        const tabs2D = setTabActiveSpectrum(draft, dataGroupByTab);
        if (tabs2D.length > 0 && tab == null) {
            draft.view.spectra.activeTab = tabs2D[0];
        }
        else {
            draft.view.spectra.activeTab = !groupByTab.includes(tab)
                ? groupByTab[0]
                : tab;
        }
    }
    else if (tab) {
        draft.view.spectra.activeTab = tab;
    }
    setDisplayerMode(draft, dataGroupByTab[draft.view.spectra.activeTab]);
    setMargin(draft);
}
function setActiveTab(draft, options) {
    const { tab = null, refreshActiveTab = false, domainOptions = {}, } = options || {};
    const groupByNucleus = groupByInfoKey('nucleus');
    const dataGroupByNucleus = groupByNucleus(draft.data, true);
    setTab(draft, dataGroupByNucleus, tab, refreshActiveTab);
    resetTool(draft);
    setDomain(draft, domainOptions);
    setIntegralsYDomain(draft, dataGroupByNucleus[draft.view.spectra.activeTab]);
    const zoomHistory = zoomHistoryManager(draft.zoom.history, draft.view.spectra.activeTab);
    const zoomValue = zoomHistory.getLast();
    if (zoomValue) {
        draft.xDomain = zoomValue.xDomain;
        draft.yDomain = zoomValue.yDomain;
    }
    setMode(draft);
}
function handelSetActiveTab(draft, tab) {
    if (tab) {
        setActiveTab(draft, { tab });
    }
}
function levelChangeHandler(draft, { deltaY, shiftKey }) {
    const { data, view: { spectra: { activeTab }, zoom: { levels }, }, } = draft;
    const activeSpectra = getActiveSpectra(draft) || [];
    const activeSpectraObj = {};
    for (const activeSpectrum of activeSpectra) {
        activeSpectraObj[activeSpectrum.id] = true;
    }
    const spectra = getSpectraByNucleus(activeTab, data).filter((spectrum) => spectrum.info.isFt &&
        (activeSpectraObj?.[spectrum.id] || activeSpectra.length === 0));
    try {
        for (const spectrum of spectra) {
            const contourOptions = spectrum.display.contourOptions;
            const zoom = contoursManager(spectrum.id, levels, contourOptions);
            levels[spectrum.id] = zoom.wheel(deltaY, shiftKey);
        }
    }
    catch (error) {
        // TODO: handle error.
        reportError(error);
    }
}
function setSpectraSameTopHandler(draft) {
    if (draft.displayerMode === DISPLAYER_MODE.DM_1D) {
        draft.originDomain.shareYDomain = false;
        setZoom(draft, { scale: 0.8 });
    }
}
function resetSpectraScale(draft) {
    draft.originDomain.shareYDomain = true;
    draft.yDomains = draft.originDomain.yDomains;
    draft.yDomain = draft.originDomain.yDomain;
    setZoom(draft, { scale: 0.8 });
}
export { resetSelectedTool, setSelectedTool, setSelectedOptionPanel, setSpectrumsVerticalAlign, handleChangeSpectrumDisplayMode, handleAddBaseLineZone, handleDeleteBaseLineZone, handleToggleRealImaginaryVisibility, handleBrushEnd, setVerticalIndicatorXPosition, handleZoom, zoomOut, handelSetActiveTab, levelChangeHandler, setActiveTab, setTab, setSpectraSameTopHandler, resetSpectraScale, setMargin, };
